import React, { useState, useEffect, useRef, useCallback } from "react";
import "./demo.css";
import { WORKFLOWS } from "./demoData";
import DemoHero from "./DemoHero";
import WorkflowSelector from "./WorkflowSelector";
import UploadZone from "./UploadZone";
import LifecycleTracker from "./LifecycleTracker";
import DemoControls from "./DemoControls";
import DocumentPreview from "./DocumentPreview";
import GuardrailsPanel from "./GuardrailsPanel";
import HumanReviewGate from "./HumanReviewGate";
import DemoAuditTrail from "./DemoAuditTrail";
import AIChangesPanel from "./AIChangesPanel";
import ViolationsPanel from "./ViolationsPanel";

async function sha256hex(text) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export default function LiveDemoModule() {
  const [theme, setTheme] = useState("dark");
  const [wfIndex, setWfIndex] = useState(null);
  const [step, setStep] = useState(0);
  const [running, setRunning] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [started, setStarted] = useState(false);
  const [file, setFile] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [approvals, setApprovals] = useState({});
  const approvalsRef = useRef({});
  const [auditEvents, setAuditEvents] = useState([]);
  const auditRef = useRef({ events: [], chain: Promise.resolve(), lastHash: null });
  const timerRef = useRef(null);

  // Hash-chained session audit trail (21 CFR §11.10(e) pattern)
  const logAudit = useCallback((action, details) => {
    const a = auditRef.current;
    a.chain = a.chain.then(async () => {
      const timestamp = new Date().toISOString();
      const previous_entry_hash = a.lastHash;
      const entry_hash = await sha256hex(
        JSON.stringify({ action, details, timestamp, previous_entry_hash })
      );
      a.lastHash = entry_hash;
      a.events = [...a.events, { action, details, timestamp, entry_hash, previous_entry_hash }];
      setAuditEvents(a.events);
    });
  }, []);

  const clearAudit = useCallback(() => {
    auditRef.current = { events: [], chain: Promise.resolve(), lastHash: null };
    setAuditEvents([]);
  }, []);

  const wf = wfIndex !== null ? WORKFLOWS[wfIndex] : null;

  const toast = useCallback((msg) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3200);
  }, []);

  const stopRun = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setRunning(false);
  }, []);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const clearApprovals = () => {
    approvalsRef.current = {};
    setApprovals({});
  };

  const selectWorkflow = (i) => {
    stopRun();
    setWfIndex(i);
    setStep(0);
    setStarted(false);
    setCompleted(false);
    clearApprovals();
    clearAudit();
    logAudit("workflow_selected", `${WORKFLOWS[i].name} (${WORKFLOWS[i].docId})`);
  };

  const firstBlockedGate = (target) => {
    for (let i = 0; i < target; i++) {
      if (wf.steps[i].gate && !approvalsRef.current[i]) return i;
    }
    return -1;
  };

  const goToStep = (n, total) => {
    stopRun();
    setStarted(true);
    const blocked = firstBlockedGate(n);
    if (blocked !== -1) {
      setStep(blocked);
      toast("🚦 Human review & e-signature required at this gate before proceeding");
      return;
    }
    setStep(n);
    if (n === total - 1) setCompleted(true);
  };

  const startRunFrom = (startIndex) => {
    stopRun();
    setRunning(true);
    let current = startIndex;
    timerRef.current = setInterval(() => {
      current += 1;
      const s = wf.steps[current];
      if (s.gate && !approvalsRef.current[current]) {
        setStep(current);
        stopRun();
        logAudit("workflow_paused_human_gate", `${s.label} — awaiting human review & e-signature`);
        toast("🚦 Workflow paused — human review & approval required to continue");
        return;
      }
      if (current >= wf.steps.length - 1) {
        setStep(wf.steps.length - 1);
        setCompleted(true);
        stopRun();
        toast(`✅ ${wf.name} workflow complete!`);
      } else {
        setStep(current);
      }
    }, 2800);
  };

  const runDemo = () => {
    if (!wf) { toast("⚠️ Select a workflow first"); return; }
    setStarted(true);
    setStep(0);
    setCompleted(false);
    logAudit("workflow_started", `${wf.name} lifecycle run initiated${file ? ` with ${file.name}` : ""}`);
    startRunFrom(0);
  };

  const approveGate = (idx, name) => {
    approvalsRef.current = { ...approvalsRef.current, [idx]: { name, time: new Date().toISOString() } };
    setApprovals(approvalsRef.current);
    logAudit("esignature_applied", `${wf.steps[idx].label} approved & e-signed by ${name} (${wf.steps[idx].reg})`);
    toast("✍️ Human approval recorded — 21 CFR Part 11 e-signature applied");
    startRunFrom(idx);
  };

  const reset = () => {
    stopRun();
    setStep(0);
    setStarted(false);
    setCompleted(false);
    setFile(null);
    clearApprovals();
    clearAudit();
  };

  const handleFile = (f) => {
    setFile(f);
    logAudit("document_uploaded", `${f.name} (${f.sizeKB} KB) received for processing`);
    toast(`📎 ${f.name} dropped — click Run Demo to process`);
  };

  // Capture every step transition (auto-run, manual navigation, and gate jumps)
  useEffect(() => {
    if (wf && started) {
      const s = wf.steps[step];
      logAudit("step_entered", `${s.icon} ${s.label} — ${s.reg}`);
    }
     
  }, [wfIndex, step, started]);

  useEffect(() => {
    if (completed && wf) {
      logAudit("workflow_completed", `${wf.name} — ${wf.completionScore} — all gates cleared`);
    }
     
  }, [completed]);

  const stepData = wf && started ? wf.steps[step] : null;

  return (
    <div className={`lcx-demo ${theme === "light" ? "light" : ""}`}>
      <DemoHero theme={theme} onToggleTheme={() => setTheme(theme === "dark" ? "light" : "dark")} />

      <div className="lcx-container" style={{ paddingBottom: "4rem" }}>
        <h2 className="lcx-sec-title">Step 1 <span className="sub">— Select a lifecycle to demo</span></h2>
        <WorkflowSelector workflows={WORKFLOWS} activeIndex={wfIndex} onSelect={selectWorkflow} />

        <h2 className="lcx-sec-title">Step 2 <span className="sub">— Optional: drop a document, or run the simulation</span></h2>
        <UploadZone file={file} onFile={handleFile} />

        {wf && (
          <>
            <h2 className="lcx-sec-title">Step 3 <span className="sub">— Watch the governed lifecycle run</span></h2>
            <div className="lcx-panel">
              <div className="lcx-box">
                <LifecycleTracker
                  wf={wf}
                  step={step}
                  started={started}
                  approvals={approvals}
                  onJump={(n) => goToStep(n, wf.steps.length)}
                />
                <DemoControls
                  running={running}
                  started={started}
                  step={step}
                  total={wf.steps.length}
                  completed={completed}
                  timeSaved={wf.timeSaved}
                  regCount={wf.regs.length}
                  onRun={runDemo}
                  onBack={() => goToStep(Math.max(0, step - 1), wf.steps.length)}
                  onNext={() => goToStep(Math.min(wf.steps.length - 1, step + 1), wf.steps.length)}
                  onReset={reset}
                />
                {started && stepData?.gate && (
                  <HumanReviewGate
                    wf={wf}
                    stepIndex={step}
                    approval={approvals[step]}
                    onApprove={(name) => approveGate(step, name)}
                  />
                )}
              </div>
              <div className="lcx-box">
                <DocumentPreview wf={wf} step={step} started={started} completed={completed} file={file} approvals={approvals} onToast={toast} />
              </div>
            </div>

            <h2 className="lcx-sec-title">🔍 Review Windows <span className="sub">— What the AI changed, and every regulatory violation flagged</span></h2>
            <div className="lcx-panel">
              <AIChangesPanel wf={wf} step={step} started={started} />
              <ViolationsPanel wf={wf} step={step} started={started} />
            </div>

            <h2 className="lcx-sec-title">🛡️ Regulatory Guardrails in Action <span className="sub">— Active controls for current step</span></h2>
            <GuardrailsPanel stepData={stepData} />

            <h2 className="lcx-sec-title">🧾 Immutable Audit Trail <span className="sub">— Hash-chained record of every workflow event (21 CFR §11.10(e))</span></h2>
            <DemoAuditTrail events={auditEvents} wf={wf} />
          </>
        )}
      </div>

      <div className="lcx-toasts">
        {toasts.map((t) => (
          <div key={t.id} className="lcx-toast">{t.msg}</div>
        ))}
      </div>
    </div>
  );
}